// Ported from nexu-io/open-design (Apache-2.0)
// https://github.com/nexu-io/open-design

export interface PetImageResult {
  dataUrl: string;
  width: number;
  height: number;
  reencoded: boolean;
}

const MAX_DATA_URL_BYTES = 800 * 1024;
const MAX_REENCODED_PX = 384;
const PASSTHROUGH_TYPES = new Set(['image/gif', 'image/svg+xml', 'image/webp']);

export async function loadPetImageFromFile(file: File): Promise<PetImageResult> {
  if (!file.type.startsWith('image/')) throw new Error('Only image files are supported.');
  if (PASSTHROUGH_TYPES.has(file.type)) {
    const dataUrl = await fileToDataUrl(file);
    if (approxDataUrlBytes(dataUrl) > MAX_DATA_URL_BYTES) throw new Error('That image is too large after encoding. Try one under ~800 KB.');
    const dims = await measureImage(dataUrl);
    return { dataUrl, width: dims.width, height: dims.height, reencoded: false };
  }
  const dataUrl = await fileToDataUrl(file);
  const original = await measureImage(dataUrl);
  const scale = Math.min(1, MAX_REENCODED_PX / Math.max(original.width, original.height));
  const targetW = Math.max(1, Math.round(original.width * scale));
  const targetH = Math.max(1, Math.round(original.height * scale));
  const reencoded = await drawToPng(dataUrl, targetW, targetH);
  if (approxDataUrlBytes(reencoded) > MAX_DATA_URL_BYTES) throw new Error('That image is too large after encoding. Try a smaller source.');
  return { dataUrl: reencoded, width: targetW, height: targetH, reencoded: true };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Read failed'));
    reader.onload = () => {
      if (typeof reader.result !== 'string') { reject(new Error('Could not decode the image.')); return; }
      resolve(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

function measureImage(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not load that image.'));
    img.src = dataUrl;
  });
}

function drawToPng(dataUrl: string, w: number, h: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas is unavailable in this browser.')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      try { resolve(canvas.toDataURL('image/png')); }
      catch (err) { reject(err instanceof Error ? err : new Error('Encode failed')); }
    };
    img.onerror = () => reject(new Error('Could not load that image.'));
    img.src = dataUrl;
  });
}

function approxDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return dataUrl.length;
  return Math.floor((dataUrl.slice(comma + 1).length * 3) / 4);
}
